import React, { useState, useEffect } from 'react';
import ResumeUploader from './ResumeUploader';
import RichTextEditor from './RichTextEditor';
import RichTextDisplay from './RichTextDisplay';
import styles from './Resume.module.css';
import { toast } from 'react-hot-toast';
import { api } from '../apiConfig';

const Resume = ({ value, rawText, onChange, onRawTextChange }) => {
  const [rawResumeText, setRawResumeText] = useState(rawText || '');
  const [showUploader, setShowUploader] = useState(false);
  const [isEditingDocument, setIsEditingDocument] = useState(false);
  const [editedRawText, setEditedRawText] = useState('');

  useEffect(() => {
    setRawResumeText(rawText || '');
  }, [rawText]);

  const handleUploadComplete = async (resumeData) => {
    try {
      if (!resumeData) {
        throw new Error('No resume data received');
      }
      
      console.log('📄 Resume upload response:', resumeData);
      
      // Extract raw text from upload response
      const rawTextData = resumeData.raw_text || resumeData || '';
      
      // Update raw text data
      setRawResumeText(rawTextData);
      setShowUploader(false);
      
      // Notify parent component
      if (onRawTextChange && rawTextData) {
        onRawTextChange(rawTextData);
      }
      
      toast.success('Resume uploaded successfully');
    } catch (error) {
      console.error('Error processing resume data:', error);
      toast.error('Failed to process resume data');
    }
  };

  const handleUploadClick = () => {
    setShowUploader(true);
  };

  const handleEditDocument = () => {
    setEditedRawText(rawResumeText);
    setIsEditingDocument(true);
  };

  const handleSaveDocument = async () => {
    try {
      // Save to backend
      const response = await api.put('/resume/text', {
        raw_text: editedRawText
      });

      // Update local state only after successful save
      setRawResumeText(editedRawText);
      if (onRawTextChange) onRawTextChange(editedRawText);
      setIsEditingDocument(false);
      toast.success('Resume document updated successfully');
    } catch (error) {
      console.error('Error saving resume text:', error);
      toast.error('Failed to save resume changes. Please try again.');
    }
  };

  const handleCancelDocumentEdit = () => {
    setEditedRawText('');
    setIsEditingDocument(false);
  };

  const handleDeleteDocument = async () => {
    if (window.confirm('Are you sure you want to delete your resume document? This action cannot be undone.')) {
      try {
        // Save deletion to backend
        await api.put('/resume/text', {
          raw_text: ''
        });

        // Update local state only after successful save
        setRawResumeText('');
        if (onChange) onChange('');
        if (onRawTextChange) onRawTextChange('');
        toast.success('Resume document deleted successfully');
      } catch (error) {
        console.error('Error deleting resume:', error);
        toast.error('Failed to delete resume. Please try again.');
      }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <button className={styles.uploadButton} onClick={handleUploadClick}>
          Upload Resume
        </button>
      </div>

      {showUploader && (
        <ResumeUploader onUploadComplete={handleUploadComplete} onCancel={() => setShowUploader(false)} />
      )}

      <div className={styles.documentView}>
        {!isEditingDocument && (
          <div className={styles.documentActions}>
            <button className={`${styles.actionButton} ${styles.editButton}`} onClick={handleEditDocument}>
              {rawResumeText ? 'Edit' : 'Create Resume'}
            </button>
            {rawResumeText && (
              <button className={`${styles.actionButton} ${styles.deleteButton}`} onClick={handleDeleteDocument}>
                Delete
              </button>
            )}
          </div>
        )}
        {isEditingDocument && (
          <div className={styles.documentActions}>
            <button className={`${styles.actionButton} ${styles.saveButton}`} onClick={handleSaveDocument}>
              Save
            </button>
            <button className={`${styles.actionButton} ${styles.cancelButton}`} onClick={handleCancelDocumentEdit}>
              Cancel
            </button>
          </div>
        )}
        {isEditingDocument ? (
          <RichTextEditor
            value={editedRawText}
            onChange={setEditedRawText}
            placeholder="Type or paste your resume here..."
            enableAI={true}
            section="resume"
          />
        ) : rawResumeText ? (
          <RichTextDisplay 
            content={rawResumeText}
            placeholder="No resume text available"
          />
        ) : (
          <div className={styles.empty}>
            No resume yet. Click "Create Resume" to type your resume, or click "Upload Resume" to upload an existing one.
          </div>
        )}
      </div>
    </div>
  );
};

export default Resume; 